/**
 * GA4 instrumentation.
 *
 * The gtag snippet and `config` call live in index.html; this module is the
 * only place that emits events, so every tracked interaction has one name and
 * one payload shape. Names follow GA4's snake_case convention.
 */

type Primitive = string | number | boolean | undefined

declare global {
  interface Window {
    gtag?: (command: string, ...args: unknown[]) => void
    dataLayer?: unknown[]
  }
}

export const MEASUREMENT_ID = 'G-5VKKH0CXVQ'

const isEnabled = () => typeof window !== 'undefined' && typeof window.gtag === 'function'

/** Emit a GA4 event. Silently no-ops when gtag is blocked or not yet loaded. */
export const track = (event: string, params: Record<string, Primitive> = {}) => {
  if (!isEnabled()) return
  const clean: Record<string, Primitive> = {}
  for (const [k, v] of Object.entries(params)) if (v !== undefined) clean[k] = v
  window.gtag!('event', event, clean)
}

/** Single-page-app pageview. GA4 does not fire these on client-side navigation. */
export const trackPageView = (path: string, title?: string) => {
  if (!isEnabled()) return
  window.gtag!('event', 'page_view', {
    page_path: path,
    page_location: window.location.href,
    page_title: title ?? document.title,
  })
}

/* ---------- The funnel ---------- */

/** Someone described a problem in the hero box. */
export const trackDiagnose = (params: {
  method: 'typed' | 'chip' | 'crash_report'
  matched: boolean
  problem_group: string
  query_length?: number
}) => track('diagnose_problem', params)

/** A diagnosis was rendered — the moment of highest intent. */
export const trackDiagnosisShown = (params: { problem_group: string; fix_count: number }) =>
  track('diagnosis_shown', params)

/** Someone opened "Explain the tech" on a result. */
export const trackExplainTech = (problemGroup: string) =>
  track('explain_tech_opened', { problem_group: problemGroup })

/** Any call to action. `location` says which one, so they can be compared. */
export const trackCtaClick = (params: {
  location: 'header' | 'hero_result' | 'pricing' | 'safety' | 'footer' | 'final'
  label: string
  plan?: string
}) => track('cta_click', params)

/** Signup submitted. */
export const trackSignupSubmit = (source: string) => track('sign_up_submit', { source })

/** Signup succeeded — mark this one as a conversion in the GA4 UI. */
export const trackSignupSuccess = (source: string) => track('sign_up', { source, method: 'email' })

export const trackSignupError = (source: string, reason: string) =>
  track('sign_up_error', { source, reason })

/** Pricing plan chosen. */
export const trackPlanSelect = (plan: 'free' | 'unlimited' | 'family' | 'per_fix') =>
  track('select_plan', { plan })

/** Crash report pasted, dropped, or the worked example run. */
export const trackCrashReport = (params: {
  method: 'paste' | 'drop' | 'example'
  failure_type: string
}) => track('crash_report_read', params)

/** Scroll depth milestones, fired once each. */
export const trackScrollDepth = (percent: 25 | 50 | 75 | 100) => track('scroll_depth', { percent })

/** A section came into view, fired once each. */
export const trackSectionView = (section: string) => track('section_view', { section })

/** Outbound link. */
export const trackOutbound = (url: string) => track('click_outbound', { link_url: url })
